import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Edit, 
  Camera, 
  TrendingUp, 
  BarChart3,
  Calendar,
  DollarSign,
  Target,
  Activity,
  Award,
  Clock,
  Star,
  Download,
  Share2,
  Shield
} from 'lucide-react'
import Navigation from '../components/Navigation'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

export default function DemoProfilePage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')

  // Mock用户数据
  const userProfile = {
    id: 1,
    username: 'demo_user',
    email: 'demo@example.com',
    fullName: '演示用户',
    bio: '这是一个演示账户，用于展示TideLab的功能。专注于量化交易策略开发和风险管理。',
    avatar: null,
    joinDate: '2024-01-01',
    lastLogin: '2024-01-20T10:30:00Z',
    level: '高级用户',
    experience: 85,
    strategies: 15,
    totalTrades: 89,
    winRate: 68.5,
    totalPnL: 12500,
    bestStrategy: '趋势跟踪策略A',
    favoriteMarket: 'BTC/USDT'
  }

  // Mock统计数据
  const performanceData = [
    { month: '1月', pnl: 2500, trades: 12 },
    { month: '2月', pnl: 3200, trades: 18 },
    { month: '3月', pnl: 1800, trades: 15 },
    { month: '4月', pnl: 4100, trades: 22 },
    { month: '5月', pnl: 2900, trades: 16 },
    { month: '6月', pnl: 3600, trades: 20 }
  ]

  const recentActivities = [
    {
      id: 1,
      type: 'strategy_created',
      title: '创建了新策略',
      description: '趋势跟踪策略C',
      time: '2024-01-20T14:30:00Z',
      icon: TrendingUp
    },
    {
      id: 2,
      type: 'trade_executed',
      title: '执行了交易',
      description: 'BTC/USDT 买入 0.5 BTC @ $42,500',
      time: '2024-01-20T10:15:00Z',
      icon: BarChart3
    },
    {
      id: 3,
      type: 'backtest_completed',
      title: '完成回测',
      description: '网格交易策略B 回测完成',
      time: '2024-01-19T16:45:00Z',
      icon: Target
    },
    {
      id: 4,
      type: 'note_added',
      title: '添加了研究笔记',
      description: '市场情绪指标分析',
      time: '2024-01-19T09:20:00Z',
      icon: Activity
    }
  ]

  const achievements = [
    {
      id: 1,
      title: '策略大师',
      description: '创建了10个以上策略',
      icon: Award,
      unlocked: true,
      progress: 100
    },
    {
      id: 2,
      title: '交易专家',
      description: '完成100次以上交易',
      icon: Star,
      unlocked: false,
      progress: 89
    },
    {
      id: 3,
      title: '风险管理师',
      description: '连续30天无重大回撤',
      icon: Shield,
      unlocked: true,
      progress: 100
    },
    {
      id: 4,
      title: '数据分析师',
      description: '完成50次回测分析',
      icon: BarChart3,
      unlocked: false,
      progress: 35
    }
  ]

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* 用户信息卡片 */}
      <div className="card">
        <div className="flex items-start space-x-6">
          <div className="relative">
            <div className="w-24 h-24 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {userProfile.fullName.charAt(0)}
            </div>
            <button className="absolute -bottom-1 -right-1 w-8 h-8 bg-violet-500 rounded-full flex items-center justify-center text-white hover:bg-violet-600 transition-colors">
              <Camera className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-ocean-primary">{userProfile.fullName}</h2>
                <p className="text-ocean-secondary">@{userProfile.username}</p>
              </div>
              <button className="btn-secondary flex items-center justify-center" title="编辑资料">
                <Edit className="w-4 h-4 mr-2 sm:mr-2" />
                <span className="hidden sm:inline">编辑资料</span>
              </button>
            </div>
            
            <p className="text-ocean-secondary mb-4">{userProfile.bio}</p>
            
            <div className="flex items-center space-x-6 text-sm text-ocean-secondary">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                加入时间: {new Date(userProfile.joinDate).toLocaleDateString('zh-CN')}
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                最后登录: {new Date(userProfile.lastLogin).toLocaleDateString('zh-CN')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
              <TrendingUp className="w-6 h-6 text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">创建策略</p>
              <p className="text-2xl font-bold text-gradient">{userProfile.strategies}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-green-500/20 rounded-xl border border-green-500/30">
              <BarChart3 className="w-6 h-6 text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">总交易次数</p>
              <p className="text-2xl font-bold text-gradient">{userProfile.totalTrades}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-purple-500/20 rounded-xl border border-purple-500/30">
              <Target className="w-6 h-6 text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">胜率</p>
              <p className="text-2xl font-bold text-gradient">{userProfile.winRate}%</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-orange-500/20 rounded-xl border border-orange-500/30">
              <DollarSign className="w-6 h-6 text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">总盈亏</p>
              <p className="text-2xl font-bold text-gradient">${userProfile.totalPnL.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 表现图表 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">月度盈亏趋势</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip />
              <Line type="monotone" dataKey="pnl" stroke="#10b981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">交易次数统计</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip />
              <Bar dataKey="trades" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )

  const renderActivityTab = () => (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-ocean-primary">最近活动</h3>
          <button className="btn-secondary flex items-center justify-center" title="导出记录">
            <Download className="w-4 h-4 mr-2 sm:mr-2" />
            <span className="hidden sm:inline">导出记录</span>
          </button>
        </div>

        <div className="space-y-4">
          {recentActivities.map((activity) => (
            <div key={activity.id} className="flex items-start space-x-4 p-4 bg-white/5 rounded-xl">
              <div className="p-2 bg-violet-500/20 rounded-lg">
                <activity.icon className="w-5 h-5 text-violet-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-ocean-primary">{activity.title}</h4>
                <p className="text-sm text-ocean-secondary">{activity.description}</p>
                <p className="text-xs text-ocean-secondary mt-1">
                  {new Date(activity.time).toLocaleString('zh-CN')}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const renderAchievementsTab = () => (
    <div className="space-y-6">
      <div className="card">
        <h3 className="text-lg font-semibold text-ocean-primary mb-6">成就系统</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {achievements.map((achievement) => (
            <div key={achievement.id} className={`p-4 rounded-xl border ${
              achievement.unlocked 
                ? 'bg-green-500/10 border-green-500/30' 
                : 'bg-white/5 border-white/20'
            }`}>
              <div className="flex items-center space-x-4">
                <div className={`p-3 rounded-lg ${
                  achievement.unlocked 
                    ? 'bg-green-500/20' 
                    : 'bg-gray-500/20'
                }`}>
                  <achievement.icon className={`w-6 h-6 ${
                    achievement.unlocked ? 'text-green-400' : 'text-gray-400'
                  }`} />
                </div>
                <div className="flex-1">
                  <h4 className={`font-medium ${
                    achievement.unlocked ? 'text-green-400' : 'text-ocean-primary'
                  }`}>
                    {achievement.title}
                  </h4>
                  <p className="text-sm text-ocean-secondary">{achievement.description}</p>
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs text-ocean-secondary mb-1">
                      <span>进度</span>
                      <span>{achievement.progress}%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          achievement.unlocked ? 'bg-green-500' : 'bg-violet-500'
                        }`}
                        style={{ width: `${achievement.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab()
      case 'activity':
        return renderActivityTab()
      case 'achievements':
        return renderAchievementsTab()
      default:
        return renderOverviewTab()
    }
  }

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      
      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 页面头部 */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/demo')}
                className="btn-secondary flex items-center justify-center"
                title="返回主页"
              >
                <ArrowLeft className="w-4 h-4 mr-2 sm:mr-2" />
                <span className="hidden sm:inline">返回主页</span>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gradient">个人中心</h1>
                <p className="text-ocean-secondary">查看您的个人信息、统计数据和成就</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <button className="btn-secondary flex items-center justify-center" title="导出数据">
              <Download className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">导出数据</span>
            </button>
            <button className="btn-primary flex items-center justify-center" title="分享档案">
              <Share2 className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">分享档案</span>
            </button>
          </div>
        </div>

        {/* 标签页导航 */}
        <div className="card mb-6">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'overview'
                  ? 'border-violet-500 text-ocean-primary'
                  : 'border-transparent text-ocean-secondary hover:text-ocean-primary'
              }`}
            >
              概览
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'activity'
                  ? 'border-violet-500 text-ocean-primary'
                  : 'border-transparent text-ocean-secondary hover:text-ocean-primary'
              }`}
            >
              活动记录
            </button>
            <button
              onClick={() => setActiveTab('achievements')}
              className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'achievements'
                  ? 'border-violet-500 text-ocean-primary'
                  : 'border-transparent text-ocean-secondary hover:text-ocean-primary'
              }`}
            >
              成就系统
            </button>
          </div>
        </div>

        {/* 标签页内容 */}
        {renderTabContent()}
      </main>
    </div>
  )
} 